
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import toast from 'react-hot-toast'
import { User, Lock, Mail, Key, Store, Trash2, AlertTriangle } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { profileApi, type UserProfile, type UpdateProfileData, type ChangePasswordData } from '@/lib/api/profile'
import { tenantProfileApi } from '@/lib/api/tenantProfile'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import Modal from '@/components/ui/modal'


// Schema for profile update
const profileSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
})

type ProfileFormValues = z.infer<typeof profileSchema>

// Schema for password change
const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'La contraseña actual es requerida'),
    newPassword: z
      .string()
      .min(8, 'La contraseña debe tener al menos 8 caracteres')
      .regex(/[A-Z]/, 'La contraseña debe contener al menos una mayúscula')
      .regex(/[a-z]/, 'La contraseña debe contener al menos una minúscula')
      .regex(/[0-9]/, 'La contraseña debe contener al menos un número'),
    confirmPassword: z.string().min(1, 'Confirma tu nueva contraseña'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  })

type PasswordFormValues = z.infer<typeof passwordSchema>

const tenantProfileSchema = z.object({
  valueProposition: z.string().max(400).optional(),
  targetAudience: z.string().max(400).optional(),
  localSlang: z.string().max(400).optional(),
})

type TenantProfileFormValues = z.infer<typeof tenantProfileSchema>

export default function ProfilePage() {
  const navigate = useNavigate()
  const { user: authUser, tenantId } = useAuthStore()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [tenantProfile, setTenantProfile] = useState<any | null>(null)
  const [loading, setLoading] = useState(true)
  const [savingProfile, setSavingProfile] = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)
  const [savingTenantProfile, setSavingTenantProfile] = useState(false)

  // Account deletion states (Meta & GDPR Compliance)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [isDeletingAccount, setIsDeletingAccount] = useState(false)


  // Profile form
  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    formState: { errors: profileErrors },
    reset: resetProfile,
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema as any),
  })

  // Password form
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors },
    reset: resetPassword,
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema as any),
  })

  const {
    register: registerTenantProfile,
    handleSubmit: handleSubmitTenantProfile,
    formState: { errors: tenantProfileErrors },
    reset: resetTenantProfile,
  } = useForm<TenantProfileFormValues>({
    resolver: zodResolver(tenantProfileSchema as any),
  })

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      setLoading(true)
      const userProfile = await profileApi.getProfile()
      setProfile(userProfile)
      
      // Set form default values
      resetProfile({
        name: userProfile.name || '',
      })

      try {
        const tp = await tenantProfileApi.get()
        setTenantProfile(tp)
        resetTenantProfile({
          valueProposition: tp?.valueProposition || '',
          targetAudience: tp?.targetAudience || '',
          localSlang: tp?.localSlang || '',
        })
      } catch {
        setTenantProfile(null)
        resetTenantProfile({
          valueProposition: '',
          targetAudience: '',
          localSlang: '',
        })
      }
    } catch (error: any) {
      console.error('Error loading profile:', error)
      toast.error('Error al cargar el perfil')
      
      // Fallback to auth store data
      if (authUser) {
        setProfile({
          id: authUser.id,
          email: authUser.email,
          name: authUser.name || '',
          role: authUser.role,
          tenantId: tenantId || undefined,
        })
        resetProfile({
          name: authUser.name || '',
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const onSubmitTenantProfile = async (data: TenantProfileFormValues) => {
    try {
      setSavingTenantProfile(true)

      const saved = await tenantProfileApi.upsert({
        valueProposition: data.valueProposition?.trim() || undefined,
        targetAudience: data.targetAudience?.trim() || undefined,
        localSlang: data.localSlang?.trim() || undefined,
      })
      setTenantProfile(saved)
      toast.success('Perfil del negocio actualizado')
    } catch (error: any) {
      console.error('Error updating tenant profile:', error)
      toast.error(error?.response?.data?.message || 'Error al actualizar el perfil del negocio')
    } finally {
      setSavingTenantProfile(false)
    }
  }

  const onSubmitProfile = async (data: ProfileFormValues) => {
    try {
      setSavingProfile(true)
      
      const updateData: UpdateProfileData = {
        name: data.name,
      }

      const updatedProfile = await profileApi.updateProfile(updateData)
      setProfile(updatedProfile)
      
      // Update auth store
      const { login } = useAuthStore.getState()
      login({
        token: useAuthStore.getState().token!,
        tenantId: useAuthStore.getState().tenantId,
        user: {
          id: updatedProfile.id,
          email: updatedProfile.email,
          name: updatedProfile.name || undefined,
          role: updatedProfile.role,
        },
      })
      
      toast.success('Perfil actualizado exitosamente')
    } catch (error: any) {
      console.error('Error updating profile:', error)
      toast.error(error?.response?.data?.message || 'Error al actualizar el perfil')
    } finally {
      setSavingProfile(false)
    }
  }

  const onSubmitPassword = async (data: PasswordFormValues) => {
    try {
      setSavingPassword(true)
      
      const passwordData: ChangePasswordData = {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword,
      }

      await profileApi.changePassword(passwordData)
      resetPassword()
      toast.success('Contraseña actualizada exitosamente')
    } catch (error: any) {
      console.error('Error changing password:', error)
      toast.error(error?.response?.data?.message || 'Error al cambiar la contraseña')
    } finally {
      setSavingPassword(false)
    }
  }

  const handleConfirmDeleteAccount = async () => {
    if (deleteConfirmText !== 'ELIMINAR') {
      toast.error('Debes escribir la palabra ELIMINAR para confirmar')
      return
    }
    if (!deletePassword) {
      toast.error('Debes ingresar tu contraseña actual')
      return
    }

    try {
      setIsDeletingAccount(true)
      await profileApi.deleteAccount(deletePassword)
      toast.success('Tu cuenta y datos han sido eliminados correctamente')
      setIsDeleteModalOpen(false)
      useAuthStore.getState().logout()
      navigate('/login')
    } catch (error: any) {
      console.error('Error deleting account:', error)
      toast.error(error?.response?.data?.message || 'Error al eliminar la cuenta')
    } finally {
      setIsDeletingAccount(false)
    }
  }


  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Cargando perfil...</div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">No se pudo cargar el perfil</div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 overflow-x-hidden">
      {/* Page Header */}
      <div className="mb-4 sm:mb-6">
        <h1 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-2 text-gray-900">Mi Perfil</h1>
        <p className="text-xs sm:text-sm lg:text-base text-gray-600">
          Gestiona tus datos personales y configuración de seguridad
        </p>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Column 1: Profile Section */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 flex-shrink-0" />
                <CardTitle className="text-base sm:text-lg">Datos Personales</CardTitle>
              </div>
              <CardDescription className="text-xs sm:text-sm">
                Actualiza tu información personal
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitProfile(onSubmitProfile)} className="space-y-4 sm:space-y-6">
                {/* Name Input */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nombre
                  </label>
                  <Input
                    id="name"
                    {...registerProfile('name')}
                    placeholder="Tu nombre completo"
                    className="w-full min-h-[44px] text-base sm:text-sm"
                  />
                  {profileErrors.name && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{profileErrors.name.message}</p>
                  )}
                </div>

                {/* Read-only Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 flex-shrink-0" />
                      Email
                    </div>
                  </label>
                  <Input
                    type="email"
                    value={profile.email}
                    disabled
                    className="w-full min-h-[44px] bg-gray-50 cursor-not-allowed text-base sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">El email no se puede modificar</p>
                </div>

                {/* Role Badge */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Rol
                  </label>
                  <div>
                    <Badge variant={profile.role || 'default'} showTranslation>
                      {profile.role?.toUpperCase() || 'N/A'}
                    </Badge>
                  </div>
                </div>

                {/* Tenant ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    ID de Tenant
                  </label>
                  <Input
                    value={profile.tenantId || 'N/A'}
                    disabled
                    className="w-full min-h-[44px] bg-gray-50 cursor-not-allowed font-mono text-xs sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">Identificador técnico del tenant</p>
                </div>

                {/* Save Button */}
                <div className="pt-2 sm:pt-4">
                  <Button type="submit" disabled={savingProfile} className="w-full min-h-[44px] touch-manipulation">
                    {savingProfile ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Store className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 flex-shrink-0" />
                <CardTitle className="text-base sm:text-lg">Perfil del negocio</CardTitle>
              </div>
              <CardDescription className="text-xs sm:text-sm">
                Contexto para la IA (propuesta de valor, audiencia y jerga)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitTenantProfile(onSubmitTenantProfile)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Propuesta de valor</label>
                  <Input
                    {...registerTenantProfile('valueProposition')}
                    placeholder="Ej: Precios bajos + entrega rápida"
                    className="w-full min-h-[44px] text-base sm:text-sm"
                  />
                  {tenantProfileErrors.valueProposition && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{tenantProfileErrors.valueProposition.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Audiencia objetivo</label>
                  <Input
                    {...registerTenantProfile('targetAudience')}
                    placeholder="Ej: jóvenes 18-30, empresas"
                    className="w-full min-h-[44px] text-base sm:text-sm"
                  />
                  {tenantProfileErrors.targetAudience && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{tenantProfileErrors.targetAudience.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Jerga local</label>
                  <Input
                    {...registerTenantProfile('localSlang')}
                    placeholder='Ej: "equipo" = celular'
                    className="w-full min-h-[44px] text-base sm:text-sm"
                  />
                  {tenantProfileErrors.localSlang && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{tenantProfileErrors.localSlang.message}</p>
                  )}
                </div>

                {tenantProfile?.updatedAt && (
                  <p className="text-xs text-gray-500">
                    Última actualización: {new Date(tenantProfile.updatedAt).toLocaleString()}
                  </p>
                )}

                <div className="pt-2">
                  <Button type="submit" disabled={savingTenantProfile} className="w-full min-h-[44px] touch-manipulation">
                    {savingTenantProfile ? 'Guardando...' : 'Guardar Perfil del Negocio'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Column 2: Security Section */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 flex-shrink-0" />
                <CardTitle className="text-base sm:text-lg">Seguridad</CardTitle>
              </div>
              <CardDescription className="text-xs sm:text-sm">
                Cambia tu contraseña para mantener tu cuenta segura
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitPassword(onSubmitPassword)} className="space-y-4">
                {/* Current Password */}
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                    <div className="flex items-center gap-2">
                      <Key className="h-4 w-4 flex-shrink-0" />
                      Contraseña Actual
                    </div>
                  </label>
                  <Input
                    id="currentPassword"
                    type="password"
                    {...registerPassword('currentPassword')}
                    placeholder="Ingresa tu contraseña actual"
                    className="w-full min-h-[44px] text-base sm:text-sm"
                  />
                  {passwordErrors.currentPassword && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">
                      {passwordErrors.currentPassword.message}
                    </p>
                  )}
                </div>

                {/* New Password */}
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nueva Contraseña
                  </label>
                  <Input
                    id="newPassword"
                    type="password"
                    {...registerPassword('newPassword')}
                    placeholder="Mínimo 8 caracteres, mayúsculas, minúsculas y números"
                    className="w-full min-h-[44px] text-base sm:text-sm"
                  />
                  {passwordErrors.newPassword && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">
                      {passwordErrors.newPassword.message}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Debe contener al menos 8 caracteres, una mayúscula, una minúscula y un número
                  </p>
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Confirmar Nueva Contraseña
                  </label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    {...registerPassword('confirmPassword')}
                    placeholder="Confirma tu nueva contraseña"
                    className="w-full min-h-[44px] text-base sm:text-sm"
                  />
                  {passwordErrors.confirmPassword && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">
                      {passwordErrors.confirmPassword.message}
                    </p>
                  )}
                </div>

                {/* Update Password Button */}
                <div className="pt-2 sm:pt-4">
                  <Button type="submit" disabled={savingPassword} className="w-full min-h-[44px] touch-manipulation">
                    {savingPassword ? 'Actualizando...' : 'Actualizar Contraseña'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Danger Zone: Account Deletion (Meta Compliance & GDPR) */}
      <div className="mt-8">
        <Card className="border-red-200 bg-red-50/40">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600 flex-shrink-0" />
              <CardTitle className="text-base sm:text-lg text-red-900">Zona de Peligro: Eliminar Cuenta</CardTitle>
            </div>
            <CardDescription className="text-xs sm:text-sm text-red-700">
              Solicitar la supresión definitiva de tu cuenta y datos personales conforme a las normativas de Meta y GDPR.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="text-xs sm:text-sm text-slate-600 max-w-xl">
                Al confirmar la eliminación, se borrarán permanentemente tus credenciales de acceso, sesiones activas y asociaciones de usuario. Esta acción es inmediata e irreversible.
              </div>
              <Button
                type="button"
                variant="destructive"
                onClick={() => setIsDeleteModalOpen(true)}
                className="bg-red-600 hover:bg-red-700 text-white min-h-[40px] px-4 text-xs sm:text-sm whitespace-nowrap shadow-xs"
              >
                Eliminar mi cuenta
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deletion Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (!isDeletingAccount) {
            setIsDeleteModalOpen(false)
            setDeletePassword('')
            setDeleteConfirmText('')
          }
        }}
        title="Confirmar Eliminación de Cuenta"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs sm:text-sm text-red-800 flex items-start gap-2.5">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <strong>¡Advertencia irreversible!</strong> Toda tu información de usuario y credenciales serán eliminadas. Si eres el único propietario de una organización con otros miembros activos, deberás transferir la propiedad antes de proceder.
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Ingresa tu contraseña actual:
            </label>
            <Input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Tu contraseña actual"
              className="w-full text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Escribe la palabra <span className="text-red-600 font-bold">ELIMINAR</span> para confirmar:
            </label>
            <Input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="ELIMINAR"
              className="w-full text-sm font-mono"
            />
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t">
            <Button
              type="button"
              variant="outline"
              disabled={isDeletingAccount}
              onClick={() => {
                setIsDeleteModalOpen(false)
                setDeletePassword('')
                setDeleteConfirmText('')
              }}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={isDeletingAccount || deleteConfirmText !== 'ELIMINAR' || !deletePassword}
              onClick={handleConfirmDeleteAccount}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeletingAccount ? 'Eliminando...' : 'Eliminar Definitivamente'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}


